'use client';
import React, {
  useState,
  useRef,
  RefObject,
  useEffect,
  FC,
  useCallback,
  useMemo,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { Element } from 'react-scroll';
import moment from 'moment';

import { useBreakpoint } from '@contexts/breakpoints';
import { getOffset } from '@utils/getElementOffset';
import Loader from '@components/Loader/Loader';
import {
  ChildDates,
  Guest,
} from '@components/ui/guest-box/guest-box-tab/GuestBox';
import MetaInfo from '@components/views/components/MetaInfo';
import InfoNote from '@components/ui/InfoNote';
import {
  TourPlanOtherContentCategoryTypes,
  TourPrebookAddRequestViewModel,
  TourPreCalculateViewModel,
  TourType,
  TourVariationViewModel,
} from '@api/index';
import { Row, Separator, SimpleButton } from '@components/formStyles';
import SummaryCard from '@components/views/tour/cards/summary/SummaryCard';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import Tab from '@components/ui/scroll-tab/ScrollTab';
import { formatMomentDate, URL_DATE_FORMAT } from '@utils/dateUtil';
import {
  addTourDetailGTM,
  addTourPackageToCartGTM,
  AddTourPackageToCartGTMProps,
} from '@utils/gtm/tour-gtm';
import OpportunityCard from '@components/cards/opportunity/OpportunityCard';
import { usePageTransitionAnimation } from '@hooks/animations';
import LetUsCallYou from '@components/ui/LetUsCallYou/LetUsCallYou';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import { getBasketFromCache } from '@hooks/cache';
import { useToggle } from '@hooks/common';
import ScrollToTop from '@shared/ScrollToTop';
import BankInstallments from '@components/ui/bank-installments/BankInstallments';
import { callService } from '@api/caller';
import BannerDetail from '@components/views/hotel/listing/components/Banner/BannerDetail';
import { BannerDetailWrapper } from '@components/views/hotel/listing/components/Banner/BannerStyle';
import ErrorInfoModal from '@components/views/components/ErrorInfoModal/ErrorInfoModal';
import useBasketInformation from '@hooks/useBasketInformation';
import ConfirmationModal from '@components/ui/modals/ConfirmationModal';
import { clearBasket } from '@utils/clearBasket';
import { getCurrencySymbol } from '@utils/utils';
import { clearBasketStorage } from '@utils/basket';
import CustomIcon from '@components/utils/CustomIcon';
import { ArrowLeftLine } from '@assets/icons';
import {
  GoBack,
  Seperator,
} from '@components/views/hotel/detail/HotelDetailStyle';
import useSearchBar from '@contexts/searchbar';
import TabMenu from '@shared/searchBar/tabMenu';
import palette from '@utils/theme/palette';
import { TOUR_DETAIL_TAB_MENU_LIST } from '@constants/constants';
import { useAppRouter } from '@contexts/router';
import MultipleImageView from '@components/galleries/multiple-image/MultipleImageView';
import { useTourPackageDetail } from '@hooks/useTourPackageDetail';
import { TourDetailPackageViewModel } from '@api/models/TourDetailPackageViewModel';
import { TourPackageDetailOtherContentViewModel } from '@api/models/TourPackageDetailOtherContentViewModel';
import TourDates, {
  TourDatesMobile,
} from '@components/views/tour/components/tour-dates';

import AboutTour from './components/AboutTour';
import {
  BreadcrumbWrapper,
  ImagesWrapper,
  Wrapper,
  TourDetailContainer,
  TabMenuWrapper,
  TourOpportunitySection,
} from './styled';
import TourItinerary from '../cards/TourItinerary/TourItinerary';
import PriceDetails from './PriceDetails';
import { useTourDetailState, useSetPromotionForPackage } from './context';
import { formatPriceDetails, formatTourPromotions } from './utils/formats';
import { formatTourImages } from '../formats';
import TourDetailContent from '../components/tour-detail-content/TourDetailContent';
import TourDetailSkeleton from './components/skeleton';
import { TourTypesSalesItemMap } from '../constants/constants';
import TourStickyHeader from './components/TourStickyHeader';
import TourMobileHeader from './components/TourDetailMobileHeader';
import TourGallery from '../components/tour-gallery/TourGallery';
import { ITourDetailProps } from '.';
import PackageTemplateContainer from './components/package-template/PackageTemplateContainer';
import {
  completePackagePrebook,
  finishPackageReserve,
  getValidBasketId,
  tryStartPackageReserve,
} from './packageReserveFlow';

const getStoredBasketId = (): string => {
  if (typeof window === 'undefined') return '';

  try {
    const basketId = localStorage.getItem('basketId');
    return getValidBasketId(basketId);
  } catch {
    return '';
  }
};

const storeBasketId = (basketId: string): void => {
  try {
    localStorage.setItem('basketId', basketId);
  } catch {
    // The bid query parameter remains the navigation source if storage is unavailable.
  }
};

type PackageReserveRequest = {
  body: TourPrebookAddRequestViewModel[];
  gtmPayload: AddTourPackageToCartGTMProps;
};

const convertContentTypeIdToString = (
  contentTypeId?: TourPlanOtherContentCategoryTypes
) => {
  switch (contentTypeId) {
    case TourPlanOtherContentCategoryTypes.SERVICES:
      return 'services';
    case TourPlanOtherContentCategoryTypes.VISA:
      return 'visa';
    case TourPlanOtherContentCategoryTypes.GENERAL_CONDITIONS:
      return 'general_conditions';
    case TourPlanOtherContentCategoryTypes.COVID_PRECAUTIONS:
      return 'covid_precautions';
    default:
      return '';
  }
};

// =============================
// Get the children for a guests
// =============================
const getChildrenBirthDates = (guests: Guest[]) => {
  return guests.reduce((acc, guest: Guest) => {
    guest.childList?.forEach((child: ChildDates) => {
      acc.push(child.date);
    });
    return acc;
  }, [] as unknown[]) as string[];
};

const TourDetail: FC<ITourDetailProps> = ({ initialTourSSRData: data }) => {
  const { xl } = useBreakpoint();
  const { push } = useAppRouter();
  const programsRef = useRef<HTMLElement>(null);
  const tourDatesRef = useRef<HTMLElement>(null);
  const searchBarRef = useRef(null);
  const aboutRef = useRef<HTMLElement>(null);
  const productViewTrackedTourIdRef = useRef<number | undefined>(undefined);
  const packageReserveInFlightRef = useRef(false);
  const basketClearInFlightRef = useRef(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(
    null
  );
  const [selectedPackageDetailId, setSelectedPackageDetailId] = useState<
    number | undefined
  >(undefined);
  const [
    selectedPackageSelectionDetailId,
    setSelectedPackageSelectionDetailId,
  ] = useState<number | undefined>(undefined);
  const [showGalleryModal, toggleGalleryModal] = useToggle(false);
  const [selectedVariationsDetails, setSelectedVariationsDetails] = useState<
    TourPreCalculateViewModel[]
  >([]);
  const { selectedVariations = [], selectedPromotions } = useTourDetailState();
  const { removePackage } = useSetPromotionForPackage();
  const [showOpportunities, handleOpportunityNavigate] = useToggle(false);
  const [startImage, setStartImage] = useState(-1);

  const { basketInfo, basketId, itemsInBasket } = useBasketInformation();
  const [showInstallments, setShowInstallments] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<HTMLElement | null>(null);
  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showLetUsCallYou, setShowLetUsCallYou] = useState(false);

  const packageSelection = useMemo<TourDetailPackageViewModel[]>(
    () => data?.packages ?? [],
    [data?.packages]
  );
  const resolvedResourceType: 'Tur' | 'Gemi' =
    data?.type === TourType.CRUISE ? 'Gemi' : 'Tur';
  const formattedTourPromotions = formatTourPromotions(
    data?.promotions ?? undefined
  );
  const shouldRenderTourPromotions =
    Boolean(data?.promotions?.length) &&
    (formattedTourPromotions.length > 0 || Boolean(data?.bankInstallments));

  const activeSelectedPackage = useMemo(() => {
    if (selectedPackageId === null) return undefined;

    return (
      packageSelection.find(
        (item) =>
          item.id === selectedPackageId &&
          (item.detailId ?? undefined) === selectedPackageSelectionDetailId
      ) || packageSelection.find((item) => item.id === selectedPackageId)
    );
  }, [packageSelection, selectedPackageId, selectedPackageSelectionDetailId]);

  useEffect(() => {
    if (!packageSelection.length) {
      setSelectedPackageId(null);
      setSelectedPackageDetailId(undefined);
      setSelectedPackageSelectionDetailId(undefined);
      return;
    }

    const currentPackage = packageSelection.find(
      (item) => item.id === selectedPackageId
    );
    if (currentPackage) return;

    const nextPackage =
      packageSelection.find((item) => item.isDefault) || packageSelection[0];
    setSelectedPackageId(nextPackage.id);
    setSelectedPackageDetailId(nextPackage.detailId ?? undefined);
    setSelectedPackageSelectionDetailId(nextPackage.detailId ?? undefined);
  }, [packageSelection, selectedPackageId]);

  const packageDetailQuery = useTourPackageDetail({
    tourId: data?.id,
    tourPackageId: activeSelectedPackage?.id || 0,
    tourPackageDetailId: selectedPackageDetailId,
    enabled: !!activeSelectedPackage?.id,
  });

  const packageDetail =
    packageDetailQuery.data?.tourPackageId === activeSelectedPackage?.id &&
    packageDetailQuery.data?.tourId === data?.id
      ? packageDetailQuery.data
      : undefined;

  // Program ve içerik verisi yalnız package-detail response'undan gelir; testing
  // kontratında `TourDetailViewModel.programs/contents` alanları bulunmuyor.
  const selectedPackageDates = packageDetail?.defaultPackageDetail;
  const selectedPackageDepartureDate = packageSelection.length
    ? selectedPackageDates?.departureDate
    : undefined;
  const detailPrograms = packageDetail?.programs ?? [];
  const detailContents = useMemo<TourPackageDetailOtherContentViewModel[]>(
    () => packageDetail?.otherContents ?? [],
    [packageDetail?.otherContents]
  );
  const sortedDetailContents = useMemo(
    () =>
      [...detailContents].sort(
        (first, second) => (first.orderNo ?? 0) - (second.orderNo ?? 0)
      ),
    [detailContents]
  );

  const handlePackageChange = useCallback(
    (packageId: number, detailId?: number) => {
      setSelectedPackageId(packageId);
      setSelectedPackageDetailId(detailId);
      setSelectedPackageSelectionDetailId(detailId);
    },
    []
  );

  const handleStayOptionChange = useCallback((packageDetailId: number) => {
    setSelectedPackageDetailId(packageDetailId);
  }, []);

  const [
    confirmFullBasketModal,
    toggleConfirmFullBasketModal,
    setConfirmFullBasketModal,
  ] = useToggle();
  const [isRemoveFlightLoading, setIsRemoveFlightLoading] = useState(false);
  const [pendingPackageReserveRequest, setPendingPackageReserveRequest] =
    useState<PackageReserveRequest | null>(null);
  const [isPackageReservePending, setIsPackageReservePending] = useState(false);
  const [showPrebookError, setShowPrebookError] = useState(false);

  const { unlockBasketAsync, cleanBasketSignal } = useUnlockBasket({
    basketId: getBasketFromCache()?.id || '',
  });

  const addBasket = async (promotions: string[] = []) => {
    const basketId = getStoredBasketId();

    const body = selectedVariations?.map(
      (variation): TourPrebookAddRequestViewModel => ({
        id: data?.id || 0,
        packageDetailId: variation.packageDetailId || 0,
        companyCommissionAmount: variation.companyCommissionAmount || 0,
        // Because of date format, childBirthdates doesn't show as nullable.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        guests: variation.guests?.map((guest) => ({
          adultCount: guest.adultNumber,
          ...(guest.childList
            ? {
                childBirthdates: guest.childList?.map((child) =>
                  formatMomentDate(child.date, '')
                ),
              }
            : {}),
        })),
        extraServices: [],
        promotions,
      })
    );

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService('PrebookService', 'prebookAddTour', {
      basketId: basketState === 'stale' || !basketId ? '' : basketId,
      body,
    });
  };

  const addPackageTemplateBasket = async (
    body: TourPrebookAddRequestViewModel[]
  ) => {
    const basketId = getStoredBasketId();

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService('PrebookService', 'prebookAddTour', {
      basketId: basketState === 'stale' || !basketId ? '' : basketId,
      body,
    });
  };

  const { isLoading: isRedirecting, hideLoading } =
    usePageTransitionAnimation();

  const handlePrebookSuccess = useCallback(
    (nextBasketId: unknown) => {
      const validBasketId = getValidBasketId(nextBasketId);
      if (!validBasketId) {
        hideLoading();
        setPendingPackageReserveRequest(null);
        setShowPrebookError(true);
        return;
      }

      storeBasketId(validBasketId);
      setPendingPackageReserveRequest(null);
      setShowPrebookError(false);
      push(`/seyahat-planlama?bid=${encodeURIComponent(validBasketId)}`);
    },
    [hideLoading, push]
  );

  const handlePrebookError = useCallback(() => {
    hideLoading();
    setPendingPackageReserveRequest(null);
    setShowPrebookError(true);
  }, [hideLoading]);

  const finishPackageReserveFlow = useCallback(() => {
    finishPackageReserve(packageReserveInFlightRef);
    setIsPackageReservePending(false);
  }, []);

  const handlePackagePrebookError = useCallback(() => {
    finishPackageReserveFlow();
    handlePrebookError();
  }, [finishPackageReserveFlow, handlePrebookError]);

  const handlePackagePrebookSuccess = useCallback(
    (nextBasketId: unknown, request: PackageReserveRequest) => {
      const completed = completePackagePrebook({
        basketId: nextBasketId,
        trackAddToCart: () => addTourPackageToCartGTM(request.gtmPayload),
        completeNavigation: (validBasketId) => {
          setIsPackageReservePending(true);
          handlePrebookSuccess(validBasketId);
        },
      });
      if (!completed) {
        handlePackagePrebookError();
      }
    },
    [handlePackagePrebookError, handlePrebookSuccess]
  );

  const prebookMutation = useMutation(
    ['tourPrebook'],
    async () => {
      const promotions = Object.keys(selectedPromotions).reduce(
        (promotionsIds, key) => {
          const index: number = +key;
          const promotion = selectedPromotions[index] ?? [];
          if (!promotion.length) return promotionsIds;
          const promotionIdsForPackage = promotion.reduce((acc, p) => {
            const promotionId = p.promotionInfoList?.[0]?.promotionId;
            if (promotionId) acc.push(promotionId);
            return acc;
          }, [] as string[]);
          return [...promotionsIds, ...promotionIdsForPackage];
        },
        [] as string[]
      );

      return addBasket(promotions);
    },
    {
      onSuccess: ({ basketId }) => handlePrebookSuccess(basketId),
      onError: handlePrebookError,
    }
  );
  const packageTemplateReserveMutation = useMutation(
    ['tourPackageTemplateReserve'],
    async (request: PackageReserveRequest) =>
      addPackageTemplateBasket(request.body),
    {
      onSuccess: ({ basketId }, request) =>
        handlePackagePrebookSuccess(basketId, request),
      onError: handlePackagePrebookError,
    }
  );
  const {
    state: { tour: state },
  } = useSearchBar();

  const handleNavigatePrograms = (): void =>
    programsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  const handleNavigateDates = (): void =>
    tourDatesRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleRemoveVariation = (id: number, packageId: number) => {
    removePackage(packageId, -1);

    selectedVariationsDetails?.length &&
      setSelectedVariationsDetails(
        selectedVariationsDetails.filter(
          (selected) => selected.packageDetailId !== id
        )
      );
  };

  useEffect(() => {
    if (!data?.id || productViewTrackedTourIdRef.current === data.id) return;

    const hasPackages = packageSelection.length > 0;
    const packageDates = packageDetail?.defaultPackageDetail;
    if (hasPackages && !packageDates?.departureDate) return;

    productViewTrackedTourIdRef.current = data.id;
    addTourDetailGTM(data, {
      resourceType: resolvedResourceType,
      departureDate: hasPackages ? packageDates?.departureDate : undefined,
      arrivalDate: hasPackages ? packageDates?.arrivalDate : undefined,
    });
  }, [data, packageDetail, packageSelection.length, resolvedResourceType]);

  // =================================
  // Get the selected packages details
  // =================================
  const getTotalSelectedPackagesDetail = useCallback(async () => {
    const promises = selectedVariations.reduce((acc, item) => {
      if (
        selectedVariationsDetails.find(
          (i) => i.packageDetailId === item.packageDetailId
        )
      )
        return acc;

      acc.push(
        callService('ToursService', 'tourPrecalculate', {
          requestBody: {
            searchParameters: {
              id: data?.id || 0,
              packageDetailId: item?.packageDetailId || 0,
              guests: [
                {
                  adultCount: item?.guests[0].adultNumber || 0,
                  childCount: item?.guests[0].childList?.length || 0,
                  childrenBirthDates: getChildrenBirthDates(item.guests),
                },
              ],
            },
          },
        })
      );
      return acc;
    }, [] as Promise<TourPreCalculateViewModel>[]);

    if (!promises.length) return;

    const result = await Promise.all(promises);
    setSelectedVariationsDetails((current) => [...current, ...result]);
  }, [data?.id, selectedVariations, selectedVariationsDetails]);

  useEffect(() => {
    if (selectedVariations?.length === 0) return;
    getTotalSelectedPackagesDetail();
  }, [getTotalSelectedPackagesDetail, selectedVariations.length]);

  // ==========================================

  // Calculate the selected packages total price

  // ==========================================
  const toggleInstallments = (): void => setShowInstallments((state) => !state);
  const packagesTotalPrice = useMemo(() => {
    const totalTL = Object.keys(selectedPromotions)

      .filter((key) => selectedPromotions[+key])

      .reduce((totalPrice, key) => {
        const index: number = +key;
        const roomPackage = selectedPromotions[index] ?? [];
        const packagePrice = roomPackage.reduce(
          (acc, p) => (p.tryTotalDiscountedSellingPrice ?? 0) + acc,
          0
        );
        return packagePrice + totalPrice;
      }, 0);
    const totalMil = Object.keys(selectedPromotions)

      .filter((key) => selectedPromotions[+key])

      .reduce((totalPrice, key) => {
        const index: number = +key;
        const roomPackage = selectedPromotions[index] ?? [];
        const packagePrice = roomPackage.reduce(
          (acc, p) => (p.milePrice ?? 0) + acc,
          0
        );
        return packagePrice + totalPrice;
      }, 0);
    const totalEUR = Object.keys(selectedPromotions)

      .filter((key) => selectedPromotions[+key])
      .reduce((totalPrice, key) => {
        const index: number = +key;
        const roomPackage = selectedPromotions[index] ?? [];
        const packagePrice = roomPackage.reduce(
          (acc, p) => (p.originalTotalDiscountedSellingPrice ?? 0) + acc,
          0
        );
        return packagePrice + totalPrice;
      }, 0);

    return {
      totalTL,
      totalMil,
      totalEUR,

      originalCurrency:
        Object.values(selectedPromotions)[0]?.[0]?.originalCurrency,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedPromotions)]);

  const checkValidContents = (contentTypeId: string) => {
    return detailContents.some((item) => item.contentTypeId === contentTypeId);
  };

  const handleNavigateAboutUs = (): void =>
    aboutRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const stickyHeaderTabs = (resource: string) => [
    { key: 'overview', title: 'Genel Bakış' },
    { key: 'features', title: 'Tarihler/Özellikler' },
    ...(detailPrograms.length
      ? [{ key: 'programs', title: `${resource} Programı` }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.SERVICES)
      ? [{ key: 'services', title: 'Hizmetler' }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.VISA)
      ? [{ key: 'visa', title: 'Vize' }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.GENERAL_CONDITIONS)
      ? [{ key: 'general_conditions', title: 'Genel Şartlar' }]
      : []),
    ...(checkValidContents(TourPlanOtherContentCategoryTypes.COVID_PRECAUTIONS)
      ? [{ key: 'covid_precautions', title: 'Covid Önlemleri' }]
      : []),
  ];

  // ====================================
  // scroll the active tab when going out of the viewport
  // ===================================

  const handleActiveScrollTab = () => {
    const targetTab = tabsWrapperRef?.current?.children
      .item(0)
      ?.querySelector('.active') as HTMLElement;

    if (targetTab) {
      setActiveTab(targetTab);
    }
  };

  useEffect(() => {
    if (activeTab) {
      const isInView =
        getOffset(activeTab).left + 0 >= 0 &&
        getOffset(activeTab).left - 0 <= window.innerWidth;

      if (isInView) return;
      tabsWrapperRef.current?.children.item(0)?.scrollTo({
        left: getOffset(activeTab).left,
        top: getOffset(activeTab).top,
        behavior: 'smooth',
      });
    }
  }, [activeTab]);

  useEffect(() => {
    window.addEventListener('scroll', handleActiveScrollTab, { passive: true });

    return () => window.removeEventListener('scroll', handleActiveScrollTab);
  }, []);

  const isTourSafe = useMemo(() => {
    return data?.features?.find((item) => item.key === 21) ? true : false;
  }, [data]);

  const toggleLetUsCallYou = useCallback(
    () => setShowLetUsCallYou((current) => !current),
    []
  );

  const scrollToBottom = () => {
    aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavigateTravelPlanning = async (): Promise<void> =>
    prebookMutation.mutate();

  // =========================================
  // Clear basket hotel items before add new one
  // =========================================
  const handleClearBasketItems = async () => {
    if (basketClearInFlightRef.current) return;
    basketClearInFlightRef.current = true;

    try {
      setIsRemoveFlightLoading(true);

      if (basketInfo && basketId) {
        if (basketInfo?.id && basketInfo.isLocked) {
          clearBasketStorage(true);
        } else {
          await unlockBasketAsync();
          await clearBasket(basketInfo, basketId);
        }
      } else {
        clearBasketStorage(true);
      }

      setConfirmFullBasketModal(false);
      if (pendingPackageReserveRequest) {
        setIsPackageReservePending(true);
        packageTemplateReserveMutation.mutate(pendingPackageReserveRequest);
      } else {
        handleNavigateTravelPlanning();
      }
    } catch {
      setConfirmFullBasketModal(false);
      finishPackageReserveFlow();
      setPendingPackageReserveRequest(null);
      clearBasketStorage(true);
      setShowPrebookError(true);
    } finally {
      basketClearInFlightRef.current = false;
      setIsRemoveFlightLoading(false);
    }
  };

  const handleTabChange = (id: number): void => {
    switch (id) {
      case 1:
        return window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

      case 2:
        return handleNavigateDates();
      case 3:
        return handleNavigatePrograms();
      case 4:
        return handleNavigateAboutUs();

      default:
        return window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
    }
  };

  const viewGallery = useCallback(
    (index?: number) => {
      if (index !== undefined) setStartImage(index);
      setTimeout(() => toggleGalleryModal());
    },
    [toggleGalleryModal]
  );

  const handlePackageReserve = useCallback(
    (
      variation: TourVariationViewModel,
      packageDetailId: number,
      guests: Guest[]
    ) => {
      if (packageReserveInFlightRef.current) return;

      if (!data?.id || !packageDetailId) {
        setShowPrebookError(true);
        return;
      }

      const body: TourPrebookAddRequestViewModel[] = [
        {
          id: data.id,
          packageDetailId,
          companyCommissionAmount: variation.companyCommissionAmount ?? 0,
          guests: guests.map((guest) => ({
            adultCount: guest.adultNumber,
            childBirthdates:
              guest.childList?.flatMap((child) =>
                child.date instanceof Date &&
                !Number.isNaN(child.date.getTime())
                  ? [formatMomentDate(child.date, URL_DATE_FORMAT)]
                  : []
              ) || [],
          })),
          extraServices: [],
          promotions: variation.promotionInfoList?.[0]?.promotionId
            ? [variation.promotionInfoList[0].promotionId]
            : [],
        },
      ];

      const request: PackageReserveRequest = {
        body,
        gtmPayload: {
          tourDetail: data,
          resourceType: resolvedResourceType,
          variation,
          packageDetailId,
          guests,
          departureDate: packageDetail?.defaultPackageDetail?.departureDate,
          arrivalDate: packageDetail?.defaultPackageDetail?.arrivalDate,
        },
      };

      if (!tryStartPackageReserve(packageReserveInFlightRef)) return;

      if ((itemsInBasket || 0) > 0) {
        setIsPackageReservePending(false);
        setPendingPackageReserveRequest(request);
        toggleConfirmFullBasketModal();
        return;
      }

      setIsPackageReservePending(true);
      packageTemplateReserveMutation.mutate(request);
    },
    [
      data,
      itemsInBasket,
      packageDetail?.defaultPackageDetail?.arrivalDate,
      packageDetail?.defaultPackageDetail?.departureDate,
      packageTemplateReserveMutation,
      resolvedResourceType,
      toggleConfirmFullBasketModal,
    ]
  );

  return (
    <TourDetailContainer>
      <ScrollToTop />
      <MetaInfo
        title={data?.metaTitle || ''}
        description={data?.metaDescription || ''}
      />
      {data?.bankInstallments &&
      Array.isArray(data?.bankInstallments) &&
      showInstallments ? (
        <BankInstallments
          bankInstallments={data.bankInstallments}
          onClose={toggleInstallments}
        />
      ) : null}

      {!xl ? <TourMobileHeader /> : null}
      {!xl && !state.allResultsURL ? null : (
        <BreadcrumbWrapper>
          {state.allResultsURL ? (
            <SimpleButton>
              <Row alignItems='center'>
                <CustomIcon
                  icon={ArrowLeftLine}
                  fontSize={xl ? 24 : 16}
                  paletteColor='grey800'
                />
                <GoBack>Tüm Sonuçları Gör</GoBack>
                {xl ? <Seperator /> : null}
              </Row>
            </SimpleButton>
          ) : null}
          {xl ? (
            <Row>
              <Breadcrumb items={data?.breadcrumbs || []} />
            </Row>
          ) : null}
        </BreadcrumbWrapper>
      )}

      <TourStickyHeader
        xl={xl === undefined ? true : !!xl}
        searchBarRef={searchBarRef}
        parentRef={tabsWrapperRef}
      >
        <Tab tabs={stickyHeaderTabs(resolvedResourceType)} />
      </TourStickyHeader>

      {isRedirecting ? (
        <TourDetailSkeleton />
      ) : (
        <>
          <ImagesWrapper>
            <MultipleImageView
              isSafe={isTourSafe}
              isSuggested={data?.features?.some(
                (item) => item.value === 'Misafirler Öneriyor'
              )}
              images={formatTourImages(data?.images?.slice(0, 5), data.name)}
              totalImages={data.images?.length}
              isGalleryOpen={showGalleryModal}
              viewGallery={() => viewGallery(0)}
            />
          </ImagesWrapper>
          <Wrapper>
            <TabMenuWrapper>
              <TabMenu
                tabs={TOUR_DETAIL_TAB_MENU_LIST}
                onTabChange={handleTabChange}
                defaultTabId={1}
                colors={{
                  passiveTextColor: palette.grey800,
                }}
              />
            </TabMenuWrapper>

            <Element name='overview' className='program'>
              <SummaryCard
                installments={data.installments}
                name={data.name || ''}
                description={selectedPackageDepartureDate || ''}
                whereToGo={data.placesToGo}
                prices={data.lowestPrice}
                onNavigate={handleNavigateDates}
                tourType={data.type}
                onReadMore={handleNavigateAboutUs}
                isEmpty={(data?.packages || []).length === 0}
                toggleLetUsCallYou={toggleLetUsCallYou}
              />
              {data?.banner?.images || data?.banner?.responsiveImages ? (
                <>
                  <Separator />
                  <Element
                    onClick={() => scrollToBottom()}
                    name='about'
                    className='element'
                  >
                    {(xl === undefined || xl) && data.banner ? (
                      <>
                        {data?.banner?.images[0].url ? (
                          <BannerDetailWrapper>
                            <BannerDetail
                              url={data.banner?.images[0]?.url}
                              alt={data.banner?.images[0]?.alt}
                            />
                          </BannerDetailWrapper>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {data?.banner?.responsiveImages[0]?.url ? (
                          <BannerDetailWrapper>
                            <BannerDetail
                              url={data.banner?.responsiveImages[0]?.url}
                              alt={data.banner?.responsiveImages[0]?.alt}
                            />
                          </BannerDetailWrapper>
                        ) : null}
                      </>
                    )}
                  </Element>
                </>
              ) : null}
              <Separator />
              {shouldRenderTourPromotions ? (
                <TourOpportunitySection>
                  <OpportunityCard
                    showInstallments={toggleInstallments}
                    items={formattedTourPromotions}
                    isBankInstallments={Boolean(data?.bankInstallments)}
                    title={`${resolvedResourceType} Fırsatları`}
                    onNavigate={handleOpportunityNavigate}
                    isShowMore={showOpportunities}
                  />
                </TourOpportunitySection>
              ) : null}
            </Element>
            <Element name='features' className='element'>
              <div ref={tourDatesRef as RefObject<HTMLDivElement>}>
                <Separator size='24px' />
                {packageSelection.length > 0 ? (
                  <PackageTemplateContainer
                    resourceType={resolvedResourceType}
                    packageSelection={packageSelection}
                    selectedPackageId={selectedPackageId || undefined}
                    selectedPackageDetailId={selectedPackageDetailId}
                    selectedPackageSelectionDetailId={
                      selectedPackageSelectionDetailId
                    }
                    packageDetail={packageDetail}
                    packageDetailLoading={
                      selectedPackageId === null ||
                      packageDetailQuery.isFetching
                    }
                    packageDetailError={packageDetailQuery.isError}
                    dayCount={data?.dayCount}
                    nightCount={data?.nightCount}
                    onPackageChange={handlePackageChange}
                    onStayOptionChange={handleStayOptionChange}
                    onReserve={handlePackageReserve}
                    onLetUsCallYou={toggleLetUsCallYou}
                    reserveDisabled={
                      isPackageReservePending ||
                      packageTemplateReserveMutation.isLoading ||
                      isRemoveFlightLoading ||
                      Boolean(pendingPackageReserveRequest)
                    }
                    reserveLoading={
                      isPackageReservePending ||
                      packageTemplateReserveMutation.isLoading
                    }
                  />
                ) : (
                  <InfoNote>Uygun tur paket bulunamamaktadır.</InfoNote>
                )}
                {packageDetail?.defaultPackageDetail ? (
                  xl ? (
                    <TourDates
                      departureDate={
                        packageDetail.defaultPackageDetail.departureDate
                      }
                      arrivalDate={
                        packageDetail.defaultPackageDetail.arrivalDate
                      }
                      departureDescription={
                        packageDetail.defaultPackageDetail.departureDescription
                      }
                      arrivalDescription={
                        packageDetail.defaultPackageDetail.arrivalDescription
                      }
                    />
                  ) : (
                    <TourDatesMobile
                      departureDate={
                        packageDetail.defaultPackageDetail.departureDate
                      }
                      arrivalDate={
                        packageDetail.defaultPackageDetail.arrivalDate
                      }
                      departureDescription={
                        packageDetail.defaultPackageDetail.departureDescription
                      }
                      arrivalDescription={
                        packageDetail.defaultPackageDetail.arrivalDescription
                      }
                    />
                  )
                ) : null}
              </div>
            </Element>
            <Separator />

            {detailPrograms.length ? (
              <>
                <div ref={programsRef as RefObject<HTMLDivElement>}>
                  <Element name='programs' className='element'>
                    <TourItinerary data={detailPrograms} />
                  </Element>
                </div>
              </>
            ) : null}

            {data.metaContent ? (
              <div ref={aboutRef as RefObject<HTMLDivElement>}>
                <Element name='about' className='element'>
                  <AboutTour description={data.metaContent} />
                  <Separator />
                </Element>
              </div>
            ) : null}
            {sortedDetailContents.length
              ? sortedDetailContents.map((item, key) => (
                  <Element
                    name={
                      convertContentTypeIdToString(item.contentTypeId) || ''
                    }
                    className='element'
                    key={`${item.contentTypeId || 'other'}-${
                      item.orderNo ?? key
                    }-${key}`}
                  >
                    <TourDetailContent
                      title={item.name || 'Ekstra Bilgiler'}
                      description={item.description}
                    />
                    <Separator />
                  </Element>
                ))
              : null}
          </Wrapper>

          <TourGallery
            visible={showGalleryModal}
            toggleModal={toggleGalleryModal}
            features={data.features}
            images={data.images}
            dayCount={data.dayCount}
            nightCount={data.nightCount}
            lowestPrice={data.lowestPrice}
            tourName={data.name || ''}
            startImage={startImage}
            videos={data.videos ?? []}
          />

          {packagesTotalPrice.totalTL ? (
            <PriceDetails
              items={formatPriceDetails(
                selectedVariations.filter(({ id }) => selectedPromotions[id])
              )}
              onNavigate={handleNavigateTravelPlanning}
              onRemove={handleRemoveVariation}
              isExpedia={
                data.type === TourType.ABROAD || data.type === TourType.CRUISE
              }
              totalPrice={packagesTotalPrice.totalTL}
              totalMilePrice={packagesTotalPrice.totalMil}
              toggleConfirmModal={toggleConfirmFullBasketModal}
              itemsInBasket={itemsInBasket || 0}
            />
          ) : null}
        </>
      )}

      {confirmFullBasketModal ? (
        <ConfirmationModal
          onCancel={() => {
            finishPackageReserveFlow();
            setPendingPackageReserveRequest(null);
            setConfirmFullBasketModal(false);
          }}
          onSubmit={handleClearBasketItems}
          content='Sepetinizde başka bir ürün bulunmakta, var olanı silip yenisini eklemek ister misiniz?'
          title='Sil'
          isConfirmDisabled={isRemoveFlightLoading}
          isConfirmLoading={isRemoveFlightLoading}
        />
      ) : null}

      {showLetUsCallYou ? (
        <LetUsCallYou
          token=''
          onClose={toggleLetUsCallYou}
          entityId={String(data?.id || '')}
          entityName={data?.name || ''}
          currency={getCurrencySymbol(data?.lowestPrice?.currency)}
          price={data?.lowestPrice?.price || 0}
          salesItemType={TourTypesSalesItemMap[data?.type || TourType.ABROAD]}
          itemCountryCode={data?.type || TourType.ABROAD}
          formCheckInDate={
            selectedPackageDates?.departureDate
              ? moment(selectedPackageDates.departureDate)
              : null
          }
          formCheckOutDate={
            selectedPackageDates?.arrivalDate
              ? moment(selectedPackageDates.arrivalDate)
              : null
          }
          hotelRoomList={[]}
        />
      ) : null}

      {prebookMutation.isLoading ||
      isPackageReservePending ||
      packageTemplateReserveMutation.isLoading ||
      isRemoveFlightLoading ||
      isRedirecting ? (
        <Loader />
      ) : null}
      {showPrebookError ? (
        <ErrorInfoModal
          title='Ooops'
          description='Sepete ürün eklemeye çalışırken bir hata oluştu, lütfen tekrar deneyiniz.'
          onCancel={() => {
            setShowPrebookError(false);
            prebookMutation.reset();
            packageTemplateReserveMutation.reset();
          }}
        />
      ) : null}
    </TourDetailContainer>
  );
};

export default TourDetail;
